import React, { useEffect, useMemo } from 'react';
import { Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';

import { formatters } from 'services';
import {
  CASH_APPROACH,
  listPlayerDividends,
  REAL_TIME_SCENARIO,
  selectors,
} from 'state';

export type CashDeskColumnKey =
  | 'name'
  | 'symbol'
  | 'amount'
  | 'client_money'
  | 'client_interest'
  | 'total_currency_interest'
  | 'collateral_amount'
  | 'auction_collateral'
  | 'outstanding'
  | 'options_net_premiums';

type CashDeskConditions = {
  clientMoney: boolean;
  deposits: boolean;
  margin: boolean;
  auctions: boolean;
  outstandingDividends: boolean;
  options: boolean;
};

const COLUMN_LABELS: Record<CashDeskColumnKey, string> = {
  name: 'Name',
  symbol: 'Symbol',
  amount: 'Available Amount',
  client_money: 'Clients',
  client_interest: 'Client Interest',
  total_currency_interest: 'Accrued interest',
  collateral_amount: 'Margin',
  auction_collateral: 'Collateral',
  outstanding: 'Outstanding DIV',
  options_net_premiums: 'Net Premiums',
};

export const cashDeskColumnKeys = ({
  clientMoney,
  deposits,
  margin,
  auctions,
  outstandingDividends,
  options,
}: CashDeskConditions): CashDeskColumnKey[] => [
  'name',
  'symbol',
  'amount',
  ...(clientMoney
    ? (['client_money', 'client_interest'] as CashDeskColumnKey[])
    : []),
  ...(deposits ? (['total_currency_interest'] as CashDeskColumnKey[]) : []),
  ...(margin ? (['collateral_amount'] as CashDeskColumnKey[]) : []),
  ...(auctions ? (['auction_collateral'] as CashDeskColumnKey[]) : []),
  ...(outstandingDividends ? (['outstanding'] as CashDeskColumnKey[]) : []),
  ...(options ? (['options_net_premiums'] as CashDeskColumnKey[]) : []),
];

const CashDeskTable: React.FC = () => {
  const dispatch = useDispatch();
  const session = useSelector(selectors.activeSession);
  const wallets = useSelector(selectors.wallets);
  const currencies = useSelector(selectors.currencies);
  const instruments = useSelector(selectors.financialInstrumentsList);
  const playableSymbols = useSelector(
    selectors.playableCurrencies(instruments),
  );
  const assets = useSelector(selectors.underlyingAssets);
  const hasOptions = useSelector(selectors.sessionHasOptions);
  const dividends = useSelector(selectors.dividends);
  const { waiting: dividendsWaiting, fetched: dividendsFetched } = useSelector(
    selectors.dividendsApi,
  );

  const outstandingDividends = Object.values(assets).some(
    asset => asset.resourcetype === 'Equity',
  );
  const columns = cashDeskColumnKeys({
    clientMoney: session.client_money,
    deposits:
      session.scenario_type === REAL_TIME_SCENARIO &&
      session.risk_approach === CASH_APPROACH,
    margin: [
      session.bond_leverage,
      session.equity_leverage,
      session.etf_equities_leverage,
      session.etf_commodities_leverage,
    ].some(leverage => Number(leverage) !== 1),
    auctions: session.show_auctions === true,
    outstandingDividends,
    options: hasOptions,
  });

  useEffect(() => {
    if (outstandingDividends && !dividendsFetched) {
      dispatch(listPlayerDividends({}));
    }
  }, [dispatch, dividendsFetched, outstandingDividends]);

  const rows = useMemo(
    () =>
      wallets
        .filter(wallet =>
          playableSymbols.includes(currencies[wallet.currency]?.symbol),
        )
        .map(wallet => ({
          ...currencies[wallet.currency],
          ...wallet,
          outstanding: Object.values(dividends)
            .filter(dividend => dividend.currency === wallet.currency)
            .reduce((total, dividend) => total + dividend.outstanding, 0),
        })),
    [currencies, dividends, playableSymbols, wallets],
  );

  return (
    <div className="markets-v4-cash-table-scroll">
      <table className="markets-v4-cash-table" aria-label="Cash wallets">
        <thead>
          <tr>
            {columns.map(column => (
              <th
                key={column}
                scope="col"
                className={
                  column === 'name' || column === 'symbol' ? '' : 'is-number'
                }
              >
                {COLUMN_LABELS[column]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.id}>
              {columns.map(column => {
                const rawValue = row[column];
                const content =
                  column === 'name' || column === 'symbol' ? (
                    rawValue
                  ) : column === 'outstanding' && dividendsWaiting ? (
                    <Spin size="small" />
                  ) : column === 'outstanding' ? (
                    formatters.commas2Digits(Number(rawValue || 0))
                  ) : (
                    formatters.commasNoDigits(Number(rawValue || 0))
                  );

                return (
                  <td
                    key={column}
                    className={
                      column === 'name' || column === 'symbol'
                        ? ''
                        : 'is-number'
                    }
                  >
                    {content}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CashDeskTable;
